/**
 * NotificationContext - Real-time notifications with SignalR
 * 
 * Provides:
 * - SignalR connection management
 * - Real-time notification delivery
 * - Notification state management
 * - Read/acknowledge actions
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAuth } from './AuthContext';
import apiClient from '../api/client';
import type {
  Notification,
  NotificationState,
  NotificationCategory,
} from '../types/notifications';

// =============================================================================
// TYPES
// =============================================================================

interface NotificationContextValue {
  /** All notifications for current user */
  notifications: Notification[];
  
  /** Toast notifications for real-time popups */
  toasts: Notification[];
  
  /** Count of unread notifications */
  unreadCount: number;
  
  /** Count of unacknowledged critical notifications */
  criticalCount: number;
  
  /** SignalR connection state */
  connectionState: 'connecting' | 'connected' | 'disconnected' | 'reconnecting';
  
  /** Loading state for initial fetch */
  loading: boolean;
  
  /** Error message if any */
  error: string | null;
  
  /** Mark a notification as read */
  markAsRead: (notificationId: string) => Promise<void>;
  
  /** Mark all notifications as read */
  markAllAsRead: () => Promise<void>;
  
  /** Acknowledge a critical notification */
  acknowledge: (notificationId: string, comment?: string) => Promise<void>;
  
  /** Dismiss a notification (hide from list, doesn't delete) */
  dismiss: (notificationId: string) => void;
  
  /** Dismiss a toast popup */
  dismissToast: (notificationId: string) => void;
  
  /** Filter notifications by category */
  filterByCategory: (category: NotificationCategory | null) => Notification[];
  
  /** Filter critical/high priority notifications */
  filterCritical: () => Notification[];
  
  /** Refresh notifications from server */
  refresh: () => Promise<void>;
}

// =============================================================================
// CONTEXT
// =============================================================================

const NotificationContext = createContext<NotificationContextValue | null>(null);

// =============================================================================
// PROVIDER
// =============================================================================

interface NotificationProviderProps {
  children: React.ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [toasts, setToasts] = useState<Notification[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [connectionState, setConnectionState] = useState<NotificationContextValue['connectionState']>('disconnected');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Derived state
  const isAuthenticated = !!user;

  // ==========================================================================
  // COMPUTED VALUES
  // ==========================================================================

  const visibleNotifications = notifications.filter(n => !dismissedIds.has(n.id));
  
  const unreadCount = visibleNotifications.filter(
    n => !['Read', 'Acknowledged'].includes(n.state || 'Pending')
  ).length;
  
  const criticalCount = visibleNotifications.filter(
    n => n.priority === 'Critical' && n.state !== 'Acknowledged'
  ).length;

  // ==========================================================================
  // SIGNALR CONNECTION
  // ==========================================================================

  const setupConnection = useCallback(async () => {
    if (!isAuthenticated || !user) return;

    // Clean up existing connection
    if (connectionRef.current) {
      await connectionRef.current.stop();
    }

    setConnectionState('connecting');

    try {
      // Get API base URL from client config
      const baseUrl = apiClient.getBaseUrl();
      
      // Check if SignalR endpoint exists before connecting
      // (prevents console spam when endpoint isn't deployed)
      try {
        const checkResponse = await fetch(`${baseUrl}/notifications?limit=1`, { method: 'HEAD' });
        if (checkResponse.status === 404) {
          console.log('[Notifications] SignalR endpoint not available - using polling only');
          setConnectionState('disconnected');
          return;
        }
      } catch {
        console.log('[Notifications] SignalR endpoint check failed - using polling only');
        setConnectionState('disconnected');
        return;
      }
      
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(`${baseUrl}/notifications/negotiate`, {
          accessTokenFactory: () => apiClient.getToken() || '',
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            // Exponential backoff: 0s, 2s, 4s, 8s, 16s, max 30s
            const delay = Math.min(Math.pow(2, retryContext.previousRetryCount) * 1000, 30000);
            return delay;
          },
        })
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      // Connection state handlers
      connection.onreconnecting(() => {
        setConnectionState('reconnecting');
        console.log('[Notifications] SignalR reconnecting...');
      });

      connection.onreconnected(() => {
        setConnectionState('connected');
        console.log('[Notifications] SignalR reconnected');
        // Refresh notifications after reconnect
        fetchNotifications();
      });

      connection.onclose(() => {
        setConnectionState('disconnected');
        console.log('[Notifications] SignalR connection closed');
      });

      // Message handlers
      connection.on('notification', (notification: Notification) => {
        console.log('[Notifications] Received:', notification.type);
        setNotifications(prev => {
          // Check for duplicate
          if (prev.some(n => n.id === notification.id)) {
            return prev;
          }
          // Add to front of list
          return [notification, ...prev];
        });
        
        // Show toast for new notifications
        setToasts(prev => [...prev, notification]);
      });

      connection.on('notificationRead', (notificationId: string) => {
        setNotifications(prev =>
          prev.map(n =>
            n.id === notificationId ? { ...n, state: 'Read' as NotificationState } : n
          )
        );
      });

      connection.on('notificationAcknowledged', (notificationId: string) => {
        setNotifications(prev =>
          prev.map(n =>
            n.id === notificationId ? { ...n, state: 'Acknowledged' as NotificationState } : n
          )
        );
      });

      // Start connection
      await connection.start();
      connectionRef.current = connection;
      setConnectionState('connected');
      console.log('[Notifications] SignalR connected');

    } catch (err) {
      console.error('[Notifications] SignalR connection error:', err);
      setConnectionState('disconnected');
      setError('Failed to connect to notifications');
      
      // Schedule reconnect attempt
      reconnectTimeoutRef.current = setTimeout(() => {
        setupConnection();
      }, 5000);
    }
  }, [isAuthenticated, user]);

  // ==========================================================================
  // DATA FETCHING
  // ==========================================================================

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated || !user) return;

    try {
      setLoading(true);
      const response = await apiClient.getNotifications();
      setNotifications(response);
      setError(null);
    } catch (err) {
      console.error('[Notifications] Fetch error:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // ==========================================================================
  // ACTIONS
  // ==========================================================================

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await apiClient.markNotificationRead(notificationId);
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, state: 'Read' as NotificationState } : n
        )
      );
    } catch (err) {
      console.error('[Notifications] Mark read error:', err);
      throw err;
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const unreadIds = visibleNotifications
        .filter(n => n.state !== 'Read' && n.state !== 'Acknowledged')
        .map(n => n.id);
      
      await Promise.all(unreadIds.map(id => apiClient.markNotificationRead(id)));
      
      setNotifications(prev =>
        prev.map(n =>
          unreadIds.includes(n.id) ? { ...n, state: 'Read' as NotificationState } : n
        )
      );
    } catch (err) {
      console.error('[Notifications] Mark all read error:', err);
      throw err;
    }
  }, [visibleNotifications]);

  const acknowledge = useCallback(async (notificationId: string, comment?: string) => {
    try {
      await apiClient.acknowledgeNotification(notificationId, comment);
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, state: 'Acknowledged' as NotificationState } : n
        )
      );
    } catch (err) {
      console.error('[Notifications] Acknowledge error:', err);
      throw err;
    }
  }, []);

  const dismiss = useCallback((notificationId: string) => {
    setDismissedIds(prev => new Set([...prev, notificationId]));
  }, []);

  const dismissToast = useCallback((notificationId: string) => {
    setToasts(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  const filterByCategory = useCallback(
    (category: NotificationCategory | null) => {
      if (!category) return visibleNotifications;
      return visibleNotifications.filter(n => n.category === category);
    },
    [visibleNotifications]
  );

  const filterCritical = useCallback(() => {
    return visibleNotifications.filter(
      n => n.priority === 'Critical' || n.priority === 'High'
    );
  }, [visibleNotifications]);

  const refresh = useCallback(async () => {
    await fetchNotifications();
  }, [fetchNotifications]);

  // ==========================================================================
  // EFFECTS
  // ==========================================================================

  // Setup SignalR connection when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      setupConnection();
      fetchNotifications();
    } else {
      // Clean up on logout
      if (connectionRef.current) {
        connectionRef.current.stop();
        connectionRef.current = null;
      }
      setNotifications([]);
      setDismissedIds(new Set());
      setConnectionState('disconnected');
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (connectionRef.current) {
        connectionRef.current.stop();
      }
    };
  }, [isAuthenticated, user, setupConnection, fetchNotifications]);

  // ==========================================================================
  // CONTEXT VALUE
  // ==========================================================================

  const value: NotificationContextValue = {
    notifications: visibleNotifications,
    toasts,
    unreadCount,
    criticalCount,
    connectionState,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    acknowledge,
    dismiss,
    dismissToast,
    filterByCategory,
    filterCritical,
    refresh,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useNotificationContext() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within NotificationProvider');
  }
  return context;
}

// Alias for convenience
export const useNotifications = useNotificationContext;
